<script setup lang="ts">
import FrameIconStatement from "@/components/frames/FrameIconStatement.vue";
import LineCheckpoint from "@/components/LineCheckpoint.vue";
import ContactFormStandard from "@/components/ContactFormStandard.vue";
import FrameDetailsContact from "@/components/frames/FrameDetailsContact.vue";
import ATitle from "@/components/io/atomic/atoms/fragments/content/ATitle.vue";

const route = useRoute()

// Check if we're on a product page
const isProductPage = computed(() => {
  return route.path.includes('/product/')
})

// Dynamic content based on page type
const defaultHairline = computed(() => 
  isProductPage.value ? 'Interested in this product?' : 'Get in touch with us'
)

const defaultPrimary = computed(() => 
  isProductPage.value ? 'Request a product consultation' : 'Contact us for a free assessment'
)

const defaultDescription = computed(() => 
  isProductPage.value 
    ? 'Our team of experts is ready to help you find the perfect solution for your needs. Contact us today to discuss this product and explore our complete range of hygiene and hospitality solutions tailored to your requirements.'
    : 'Experience the Best in the Business: Reach out for a complimentary assessment. Let us showcase our unmatched expertise - at no cost to you.'
)

withDefaults(defineProps<{
  debug?: boolean
  dir?: 'ltr' | 'rtl'
  statementHairline?: string
  statementPrimary?: string
  statementDescription?: string
}>(), { 
  debug: false,
  dir: 'ltr',
})
</script>

<template>
  <Section class="sec-contact-rows gpb-4"
    w="grid ds:g-2">

    <FrameIconStatement class="row-title gmb-1">
      <template #icon>
        <LineCheckpoint id="chkcontact" />
      </template>

      <template #statement>
        <ATitle class="sec-offerings-title max-w-96% ds:max-w-80% lt-t:mx-auto" 
          :hairline="statementHairline || defaultHairline" 
          :primary="statementPrimary || defaultPrimary"
          :description="statementDescription || defaultDescription">
          <template #hairline>
            <div class="a-title-hairline fs-0! gmb-0.5 text-[var(--base-600)]!">
              {{statementHairline || defaultHairline}}
            </div>
          </template>
          <template #primary>
            <div class="a-title-primary fs-5! gmb-0.5 glh-2! gmb-1">
              {{statementPrimary || defaultPrimary}}
            </div>
          </template>
          <template #description>
            <div class="a-title-description fs-0! text-[var(--base-600)]!">
              {{ statementDescription || defaultDescription }}
            </div>
          </template>
        </ATitle>

      </template>
    </FrameIconStatement>

    <!-- contains FrameIconStatement -->
    <FrameDetailsContact>
      <template #content>
        <Section class="sec-details-contact" w="
        grid grid-cols-[1fr] g-2
        ds:grid-cols-[1fr_2fr]
        
        ">
          <Frame class="col-details" w="grid">
            <Frame class="sec-contact-details"
          w="grid grid-cols-[1fr] t:grid-cols-[1fr_1fr] g-1.5 ds:grid-cols-[1fr] ds:g-2">
          <div class="sec-detail email">
            <div class="icon w-58px">
              <div class="inner">
                <i class="i i-mdi:email"></i>
              </div>
            </div>
            <div class="detail">
              <div class="title">
                Send us an email
              </div>
              <div class="value list">
                <div>
                  <!-- <span class="lt-mlx:hidden!">Sales:</span> -->
                  <a href="mailto:info@altracarelimited.com">info@altracarelimited.com</a>
                </div>
              </div>
            </div>
          </div>
          <div v-if="false" class="sec-detail contact">
            <div class="icon">
              <div class="inner">
                <i class="i i-mdi:phone"></i>
              </div>
            </div>
            <div class="detail">
              <div class="title">
                Reach out to us
              </div>
              <div class="value">
                <a class="fs-0" href="mailto:0120125157">012 012 5157</a>
              </div>
            </div>
          </div>
          <!-- <div class="sec-detail address">
            <div class="icon">
              <div class="inner">
                <i class="i i-mdi:home"></i>
              </div>
            </div>
            <div class="detail">
              <div class="title">
                Visit us in person
              </div>
              <div class="value">
                <a>
                  Kameelfontein - Pretoria - 0129 - South Africa
                </a>
              </div>
            </div>
          </div> -->
        </Frame>
        <Frame class="sec-socials" w="grid g-1">

          <div class="sec-title gpt-2">
            <div class="small">
              Follow us
            </div>
          </div>
          <div class="sec-icons flex g-1">
            <!-- <div class="social facebook">
              <a href="#"><i class="inline-block i i-mdi:facebook"></i></a>
            </div> -->
            <!-- <div class="social twitter">
              <a href="#"><i class="inline-block i i-mdi:twitter"></i></a>
            </div>
            <div class="social linkedin">
              <a href="#"><i class="inline-block i i-mdi:linkedin"></i></a>
            </div> -->
            <div class="social instagram">
              <a href="https://www.instagram.com/altracarelimited/" target="_blank"><i class="inline-block i i-mdi:instagram"></i></a>
            </div>
          </div>

        </Frame>
          </Frame>
          <Frame class="col-contact-form" w="t:max-w-85% mx-auto">
            <ContactFormStandard />
          </Frame>
        </Section>
      </template>
    </FrameDetailsContact>
  </Section>
</template>

<style lang="scss" scoped>


.sec-offerings-title {
  :deep(.a-title-primary) {
    font-family: var(--font-secondary);
    color: var(--a-dark-blue);
  }
}

.sec-details-contact {

  .sec-contact-details {
    .sec-detail {
      .icon {
        .inner {
          color: var(--base-400);
        }
      }

      .title {
        color: var(--c-sky-blue);
      }

      .detail {
        .value {
          a {
            color: var(--base-600);
          }

          &.list {
            display:grid;
            gap: gr(0.1125);
            > div {
              display:flex;
              align-items: center;
              gap: gr(0.25);

              span {
                color: var(--base-600);
              }
              a, span {
                font-size: fs(-0);
              }
              a {
                color: var(--base-600);
              }
            }
          }
        }
      }
    }
  }
  .sec-socials {

    @include mq($until: txs) {
      display:none;
    }
    .sec-title {
     color: var(--base-400);
     font-size: fs(-0.5);

    }

    .sec-icons {
      .social {
        a {
          color: var(--base-600);

          &:hover {
            color: var(--c-sky-blue);
          }
        }
      }
    }
  }
}

</style>